"use client";

import { type ReactNode } from "react";
import type {
  CurrencyFormatSettings,
  Permalinks,
  TranslationEntries,
} from "@tripcart/shared";
import type { StorefrontFormatSettings } from "@tripcart/shared/format";
import { Toaster } from "@/components/toaster";
import { CartProviderLive } from "@/lib/cart/cart-context";
import { CurrencyFormatProvider } from "@/lib/currency-format-context";
import { DateTimeFormatProvider } from "@/lib/datetime-format-context";
import { PermalinksProvider } from "@/lib/permalinks-context";
import { TranslationProvider } from "@/lib/translation-context";

/**
 * Client-side provider stack for the `sites/[agency]` ISR route tree
 * (storefront ISR refactor, Task 5D-2 — see
 * docs/superpowers/specs/2026-07-01-storefront-root-layout-restructure-design.md).
 *
 * Same stack as `app/providers.tsx`'s `<Providers>` (translation → currency →
 * cart + Toaster), except the cart layer is `CartProviderLive` — there is no
 * server `cartPromise` to seed from on this tree (the layout resolves tenancy
 * from `params.agency`, never `cookies()`), so `CartProviderLive` client-fetches
 * `/api/cart` on mount instead (Task 5B). `currencySettings`/`translationEntries`
 * are seeded from the already-resolved `agency` param (see the `sites` layout)
 * rather than `headers()`, so mounting this adds no dynamic-API dependency.
 */
export function SitesProviders({
  children,
  currencySettings,
  dateTimeSettings,
  permalinks,
  translationEntries,
}: {
  children: ReactNode;
  currencySettings: CurrencyFormatSettings;
  dateTimeSettings: StorefrontFormatSettings;
  permalinks?: Permalinks;
  translationEntries: TranslationEntries;
}) {
  return (
    <TranslationProvider entries={translationEntries}>
      <CurrencyFormatProvider settings={currencySettings}>
        <DateTimeFormatProvider settings={dateTimeSettings}>
          <PermalinksProvider permalinks={permalinks}>
            <CartProviderLive>
              {children}
              <Toaster />
            </CartProviderLive>
          </PermalinksProvider>
        </DateTimeFormatProvider>
      </CurrencyFormatProvider>
    </TranslationProvider>
  );
}
